import { useReducer, useEffect, createContext } from 'react';

const SECTIONS_STORAGE_KEY = 'its-my-news:preferences:sections';
const AUTHORS_STORAGE_KEY = 'its-my-news:preferences:authors';
const FOLLOWING_STORAGE_KEY = 'its-my-news:preferences:following';

export const DEFAULT_SECTIONS = ['Ireland', 'Business'];
export const DEFAULT_AUTHORS = [];
// The Following tab starts on: its empty state is the columnist picker, which
// is how the feature introduces itself. Turning it off lives in the sheet.
export const DEFAULT_FOLLOWING_ENABLED = true;

function loadStored(key, fallback, isValid) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    const parsed = JSON.parse(raw);
    if (isValid(parsed)) return parsed;
  } catch {
    // Unavailable or corrupt storage: fall through to the defaults.
  }
  return fallback;
}

const isStringList = (value) =>
  Array.isArray(value) && value.every((s) => typeof s === 'string');
const isBool = (value) => typeof value === 'boolean';

function initState() {
  return {
    preferences: {
      isOpen: false,
      // Ordered list of the user's sections. "Recommended" is implicit and
      // always first; it is not stored here.
      sections: loadStored(SECTIONS_STORAGE_KEY, DEFAULT_SECTIONS, isStringList),
      // Columnists the user follows, managed from inside the Following tab.
      // Unordered as far as the UI is concerned: the Following feed is
      // sorted by date, not by author.
      authors: loadStored(AUTHORS_STORAGE_KEY, DEFAULT_AUTHORS, isStringList),
      // Whether the Following tab is shown at all; set from the sheet.
      followingEnabled: loadStored(
        FOLLOWING_STORAGE_KEY,
        DEFAULT_FOLLOWING_ENABLED,
        isBool,
      ),
    },
  };
}

function appReducer(state, action) {
  switch (action.type) {
    case 'OPEN_PREFERENCES':
      return {
        ...state,
        preferences: { ...state.preferences, isOpen: true },
      };

    case 'CLOSE_PREFERENCES':
      return {
        ...state,
        preferences: { ...state.preferences, isOpen: false },
      };

    case 'SET_SECTIONS': {
      const { sections } = action.payload;

      return {
        ...state,
        preferences: { ...state.preferences, sections },
      };
    }

    case 'SET_AUTHORS': {
      const { authors } = action.payload;

      return {
        ...state,
        preferences: { ...state.preferences, authors },
      };
    }

    case 'SET_FOLLOWING_ENABLED': {
      const { followingEnabled } = action.payload;

      return {
        ...state,
        preferences: { ...state.preferences, followingEnabled },
      };
    }

    default:
      return state;
  }
}

export const AppStateContext = createContext(null);

export function AppStateProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, null, initState);

  useEffect(() => {
    try {
      localStorage.setItem(
        SECTIONS_STORAGE_KEY,
        JSON.stringify(state.preferences.sections),
      );
    } catch {
      // Storage unavailable: selections just won't survive a refresh.
    }
  }, [state.preferences.sections]);

  useEffect(() => {
    try {
      localStorage.setItem(
        AUTHORS_STORAGE_KEY,
        JSON.stringify(state.preferences.authors),
      );
    } catch {
      // Storage unavailable: follows just won't survive a refresh.
    }
  }, [state.preferences.authors]);

  useEffect(() => {
    try {
      localStorage.setItem(
        FOLLOWING_STORAGE_KEY,
        JSON.stringify(state.preferences.followingEnabled),
      );
    } catch {
      // Storage unavailable: the choice just won't survive a refresh.
    }
  }, [state.preferences.followingEnabled]);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
}

AppStateProvider.displayName = 'AppStateProvider';
